import { STRATEGY_KEYS, type StrategyKey } from './channel-binding';
import type { Channel } from './channel-connection';

// A strategy interprets one channel's inbound events, so the routing identity is the pair
// (channel, strategy) — not the strategy alone. A binding naming a strategy its connection's
// channel never emits is dead routing: nothing dispatches it, and it still spends that
// connection's one slot for the strategy, so a real binding added later is refused for a row
// that does nothing. This map lives in the domain model because the write contract must refuse
// the pair on the server while the admin picker offers only what the channel can run; a second
// copy of it in the admin app is what let an `sbc` connection store a `reply-to-dm` binding
// (ENG-2119). An empty list means no channel can carry the strategy: a retired one declares none.
export const STRATEGY_CHANNELS: Record<StrategyKey, readonly Channel[]> = {
  'reply-to-dm': ['instagram'],
  'fan-out-on-own-media-comment': ['instagram'],
  'reply-to-mention': ['instagram'],
  'investigate-on-flag': [],
  'draft-on-inbound': [
    'instagram',
    'facebook',
    'facebook_group',
    'quo',
    'whatsapp',
    'klaviyo_sms',
    'tiktok',
  ],
  'draft-cs-reply': ['intercom'],
  'run-on-request': ['sbc'],
  'reply-when-addressed': ['slack'],
  'reply-to-every-message': ['slack'],
  'append-to-every-message': ['slack'],
  'reply-to-shortcut': ['slack'],
};

export function strategyKeysFor(channel: Channel): StrategyKey[] {
  return STRATEGY_KEYS.filter((key) => STRATEGY_CHANNELS[key].includes(channel));
}

export function channelCanRunStrategy(channel: Channel, strategyKey: StrategyKey): boolean {
  return STRATEGY_CHANNELS[strategyKey].includes(channel);
}

// The strategies that watch one external sub-scope (a Slack channel) rather than answering
// wherever they are reached. Only these may carry a binding `externalScope`; the write refuses it
// on any other strategy, where it would save cleanly and then never match an event.
const CHANNEL_SCOPED_STRATEGIES: ReadonlySet<StrategyKey> = new Set([
  'reply-to-every-message',
  'append-to-every-message',
]);

export function strategyTakesScope(strategyKey: StrategyKey): boolean {
  return CHANNEL_SCOPED_STRATEGIES.has(strategyKey);
}
